import React, { useState } from 'react';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Button } from '../ui/Button';
import { Plus, X, AlertCircle, CheckCircle } from 'lucide-react';
interface CreditScoreTier {
  id: string;
  minCreditScore: string;
  maxCreditScore: string;
  minLoanAmount: string;
  maxLoanAmount: string;
  defaultLoanAmount: string;
}
export function LoanAmountTab() {
  const [currency, setCurrency] = useState('PKR');
  const [overallMinAmount, setOverallMinAmount] = useState('');
  const [overallMaxAmount, setOverallMaxAmount] = useState('');
  const [overallDefaultAmount, setOverallDefaultAmount] = useState('');
  const [useCreditScoring, setUseCreditScoring] = useState(false);
  const [creditScoreTiers, setCreditScoreTiers] = useState<CreditScoreTier[]>([]);
  const currencyOptions = [{
    value: 'PKR',
    label: 'PKR - Pakistani Rupee'
  }];
  const addCreditScoreTier = () => {
    const lastTier = creditScoreTiers[creditScoreTiers.length - 1];
    const newMinScore = lastTier ? (parseInt(lastTier.maxCreditScore) + 1).toString() : '300';
    const newTier: CreditScoreTier = {
      id: Date.now().toString(),
      minCreditScore: newMinScore,
      maxCreditScore: '',
      minLoanAmount: overallMinAmount,
      maxLoanAmount: overallMaxAmount,
      defaultLoanAmount: ''
    };
    setCreditScoreTiers([...creditScoreTiers, newTier]);
  };
  const removeCreditScoreTier = (id: string) => {
    setCreditScoreTiers(creditScoreTiers.filter(tier => tier.id !== id));
  };
  const updateCreditScoreTier = (id: string, field: keyof CreditScoreTier, value: string) => {
    setCreditScoreTiers(creditScoreTiers.map(tier => tier.id === id ? {
      ...tier,
      [field]: value
    } : tier));
  };
  const validateTier = (tier: CreditScoreTier, index: number): string[] => {
    const errors: string[] = [];
    const overallMin = parseFloat(overallMinAmount) || 0;
    const overallMax = parseFloat(overallMaxAmount) || 0;
    const minScore = parseInt(tier.minCreditScore) || 0;
    const maxScore = parseInt(tier.maxCreditScore) || 0;
    const minAmount = parseFloat(tier.minLoanAmount) || 0;
    const maxAmount = parseFloat(tier.maxLoanAmount) || 0;
    const defaultAmount = parseFloat(tier.defaultLoanAmount) || 0;
    // Validate credit score range
    if (maxScore <= minScore) {
      errors.push('Max credit score must be greater than min credit score');
    }
    // Validate against previous tier
    if (index > 0) {
      const prevTier = creditScoreTiers[index - 1];
      const prevMaxScore = parseInt(prevTier.maxCreditScore) || 0;
      if (minScore <= prevMaxScore) {
        errors.push(`Min credit score must be greater than previous tier's max (${prevMaxScore})`);
      }
    }
    // Validate loan amounts are within overall range
    if (minAmount < overallMin) {
      errors.push(`Min loan amount must be >= overall min (${overallMin})`);
    }
    if (maxAmount > overallMax) {
      errors.push(`Max loan amount must be <= overall max (${overallMax})`);
    }
    if (minAmount > maxAmount) {
      errors.push('Min loan amount must be <= max loan amount');
    }
    // Validate default amount
    if (defaultAmount && (defaultAmount < minAmount || defaultAmount > maxAmount)) {
      errors.push('Default amount must be between min and max loan amounts');
    }
    return errors;
  };
  const getAllErrors = (): string[] => {
    const errors: string[] = [];
    creditScoreTiers.forEach((tier, index) => {
      const tierErrors = validateTier(tier, index);
      if (tierErrors.length > 0) {
        errors.push(`Tier ${index + 1}: ${tierErrors.join(', ')}`);
      }
    });
    return errors;
  };
  const allErrors = getAllErrors();
  const hasErrors = allErrors.length > 0;
  return <div className="space-y-6 max-w-4xl animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-785">
      {/* Overall Loan Amount Configuration */}
      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-786">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-787">
          Overall Loan Amount Configuration
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-788">
          <div className="md:col-span-1" data-id="element-789">
            <Select label="Currency" value={currency} onChange={e => setCurrency(e.target.value)} options={currencyOptions} data-id="element-790" />
          </div>

          <Input type="number" label="Minimum Loan Amount" placeholder="0.00" value={overallMinAmount} onChange={e => setOverallMinAmount(e.target.value)} required data-id="element-791" />

          <Input type="number" label="Maximum Loan Amount" placeholder="0.00" value={overallMaxAmount} onChange={e => setOverallMaxAmount(e.target.value)} required data-id="element-792" />

          <Input type="number" label="Default Loan Amount" placeholder="0.00" value={overallDefaultAmount} onChange={e => setOverallDefaultAmount(e.target.value)} data-id="element-793" />
        </div>
      </div>

      {/* Credit Score Based Configuration */}
      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-794">
        <div className="flex items-center justify-between mb-4" data-id="element-795">
          <div data-id="element-796">
            <h3 className="text-lg font-semibold text-gray-900" data-id="element-797">
              Credit Score Based Loan Amounts
            </h3>
            <p className="text-sm text-gray-500 mt-1" data-id="element-798">
              Configure loan amounts based on credit score ranges
            </p>
          </div>
          <label className="flex items-center space-x-2 cursor-pointer" data-id="element-799">
            <input type="checkbox" checked={useCreditScoring} onChange={e => setUseCreditScoring(e.target.checked)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-800" />
            <span className="text-sm font-medium text-gray-700" data-id="element-801">Enable</span>
          </label>
        </div>

        {useCreditScoring && <>
            {!overallMinAmount || !overallMaxAmount ? <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start space-x-3" data-id="element-802">
                <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" data-id="element-803" />
                <div data-id="element-804">
                  <p className="text-sm font-medium text-yellow-900" data-id="element-805">
                    Overall Limits Required
                  </p>
                  <p className="text-xs text-yellow-700 mt-1" data-id="element-806">
                    Please set overall minimum and maximum loan amounts before
                    configuring credit score tiers.
                  </p>
                </div>
              </div> : <>
                {/* Credit Score Tiers */}
                <div className="space-y-4" data-id="element-807">
                  {creditScoreTiers.map((tier, index) => {
              const tierErrors = validateTier(tier, index);
              const hasError = tierErrors.length > 0;
              return <div key={tier.id} className={`bg-gray-50 p-4 rounded-lg border-2 ${hasError ? 'border-red-200' : 'border-gray-200'} relative animate-in fade-in slide-in-from-bottom-2 duration-300`} data-id="element-808">
                        <div className="absolute -top-3 left-4 bg-blue-600 text-white px-3 py-0.5 rounded text-xs font-semibold" data-id="element-809">
                          Tier {index + 1}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mt-2" data-id="element-810">
                          {/* Credit Score Range */}
                          <div className="md:col-span-2" data-id="element-811">
                            <Input label="Min Credit Score" type="number" value={tier.minCreditScore} onChange={e => updateCreditScoreTier(tier.id, 'minCreditScore', e.target.value)} min={index > 0 ? parseInt(creditScoreTiers[index - 1].maxCreditScore) + 1 : 300} max="850" placeholder="300" disabled={index > 0} data-id="element-812" />
                          </div>

                          <div className="md:col-span-2" data-id="element-813">
                            <Input label="Max Credit Score" type="number" value={tier.maxCreditScore} onChange={e => updateCreditScoreTier(tier.id, 'maxCreditScore', e.target.value)} min={parseInt(tier.minCreditScore) + 1} max="850" placeholder="850" data-id="element-814" />
                          </div>

                          {/* Loan Amount Range */}
                          <div className="md:col-span-2" data-id="element-815">
                            <Input label="Min Loan Amount" type="number" value={tier.minLoanAmount} onChange={e => updateCreditScoreTier(tier.id, 'minLoanAmount', e.target.value)} min={overallMinAmount} max={overallMaxAmount} placeholder={overallMinAmount} data-id="element-816" />
                          </div>

                          <div className="md:col-span-2" data-id="element-817">
                            <Input label="Max Loan Amount" type="number" value={tier.maxLoanAmount} onChange={e => updateCreditScoreTier(tier.id, 'maxLoanAmount', e.target.value)} min={overallMinAmount} max={overallMaxAmount} placeholder={overallMaxAmount} data-id="element-818" />
                          </div>

                          <div className="md:col-span-3" data-id="element-819">
                            <Input label="Default Loan Amount" type="number" value={tier.defaultLoanAmount} onChange={e => updateCreditScoreTier(tier.id, 'defaultLoanAmount', e.target.value)} placeholder="Optional" data-id="element-820" />
                          </div>

                          {/* Remove Button */}
                          <div className="md:col-span-1 flex items-end justify-end" data-id="element-821">
                            <Button variant="ghost" size="sm" onClick={() => removeCreditScoreTier(tier.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-id="element-822">
                              <X className="h-4 w-4" data-id="element-823" />
                            </Button>
                          </div>
                        </div>

                        {/* Tier Errors */}
                        {hasError && <div className="mt-3 bg-red-50 border border-red-200 rounded p-3" data-id="element-824">
                            <div className="flex items-start space-x-2" data-id="element-825">
                              <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" data-id="element-826" />
                              <div className="text-xs text-red-700" data-id="element-827">
                                {tierErrors.map((error, i) => <div key={i} data-id="element-828">• {error}</div>)}
                              </div>
                            </div>
                          </div>}
                      </div>;
            })}
                </div>

                {/* Add Tier Button */}
                <div className="mt-4" data-id="element-829">
                  <Button variant="secondary" onClick={addCreditScoreTier} className="w-full" data-id="element-830">
                    <Plus className="h-4 w-4 mr-2" data-id="element-831" />
                    Add Credit Score Tier
                  </Button>
                </div>

                {/* Validation Summary */}
                {creditScoreTiers.length > 0 && <div className="mt-4" data-id="element-832">
                    {hasErrors ? <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3" data-id="element-833">
                        <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-834" />
                        <div data-id="element-835">
                          <p className="text-sm font-medium text-red-900" data-id="element-836">
                            Validation Errors
                          </p>
                          <div className="text-xs text-red-700 mt-2 space-y-1" data-id="element-837">
                            {allErrors.map((error, i) => <div key={i} data-id="element-838">• {error}</div>)}
                          </div>
                        </div>
                      </div> : <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3" data-id="element-839">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-840" />
                        <div data-id="element-841">
                          <p className="text-sm font-medium text-green-900" data-id="element-842">
                            Valid Configuration
                          </p>
                          <p className="text-xs text-green-700 mt-1" data-id="element-843">
                            All credit score tiers are properly configured
                          </p>
                        </div>
                      </div>}
                  </div>}
              </>}
          </>}
      </div>

      {/* Information Panel */}
      <div className="bg-blue-50 p-4 rounded-md border border-blue-100" data-id="element-844">
        <h4 className="text-sm font-semibold text-blue-800 mb-2" data-id="element-845">
          Configuration Guidelines
        </h4>
        <ul className="text-sm text-blue-600 space-y-1" data-id="element-846">
          <li data-id="element-847">
            • Default amount will be pre-filled in application forms. Ensure it
            falls within the minimum and maximum range.
          </li>
          <li data-id="element-848">• All amounts are in {currency}.</li>
          {useCreditScoring && <>
              <li data-id="element-849">
                • Credit score tiers allow different loan amounts based on
                applicant's credit score
              </li>
              <li data-id="element-850">
                • Each tier's min credit score must be greater than the previous
                tier's max
              </li>
              <li data-id="element-851">
                • Loan amounts in each tier must be within overall min/max
                limits
              </li>
              <li data-id="element-852">
                • Tiers are evaluated in order from lowest to highest score
              </li>
            </>}
        </ul>
      </div>
    </div>;
}